/**
 * AdvisorPanel - AI advisor chat for ONE finding, rendered inside the finding drawer (T4 AdvisorSlot).
 *
 * Props (stable contract for T4):
 *   jobId, findingId     the finding the thread belongs to (one thread per tenant + job + finding, server side)
 *   findingTitle?        shown in the panel heading (already localised by the caller)
 *   canAsk?              false for viewers: the thread is read-only (default true; the server enforces it anyway)
 *   onAction?            the user picked a suggested next step: 'resolve' | 'assisted_content' | 'book_expert'.
 *                        Without it, 'book_expert' opens the built-in BookExpertDialog and the others are hidden.
 *   initial?             preloaded AdvisorState (SSR / tests); skips the first fetch
 *   api?                 injected client (tests); defaults to advisorApi()
 *   headingLevel?        2 | 3 | 4 (default 3) so the panel fits the drawer's outline
 *
 * Accessibility: labelled region, the message list is a polite live region, the question box has a visible label,
 * hint and error wiring, all actions are real buttons (44 px from @qm/ui), works in EN and AR (RTL, logical CSS).
 */
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { Badge, Button, ErrorState, Loading } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import {
  advisorApi, advisorErrorKey, applyAnswer, formatWhen, messageNotes, QUESTION_MAX, questionProblem,
  type AdvisorApi, type AdvisorMessage, type AdvisorState, type SuggestedAction,
} from './api';
import { BookExpertDialog } from './BookExpertDialog';
import './advisor.css';

export interface AdvisorPanelProps {
  jobId: string;
  findingId: string;
  findingTitle?: string;
  canAsk?: boolean;
  onAction?: (action: SuggestedAction) => void;
  initial?: AdvisorState;
  api?: AdvisorApi;
  headingLevel?: 2 | 3 | 4;
}

export function AdvisorPanel({ jobId, findingId, findingTitle, canAsk = true, onAction, initial, api, headingLevel = 3 }: AdvisorPanelProps) {
  const { t, locale } = useApp();
  const client = useMemo(() => api ?? advisorApi(), [api]);
  const [state, setState] = useState<AdvisorState | null>(initial ?? null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [booking, setBooking] = useState(false);
  const id = useId();
  const H = `h${headingLevel}` as 'h2' | 'h3' | 'h4';

  useEffect(() => {
    if (initial) return;
    let live = true;
    client.thread(jobId, findingId).then((s) => { if (live) setState(s); }, (e: unknown) => { if (live) setLoadError(advisorErrorKey(e)); });
    return () => { live = false; };
  }, [client, jobId, findingId, initial]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!state) return;
    const problem = questionProblem(question);
    if (problem) { setFormError(problem); return; }
    setPending(true);
    setFormError(null);
    try {
      const r = await client.ask({ jobId, findingId, question: question.trim(), locale, ...(state.thread ? { threadId: state.thread.id } : {}) });
      setState(applyAnswer(state, r));
      setQuestion('');
    } catch (err) {
      setFormError(advisorErrorKey(err));
    } finally {
      setPending(false);
    }
  }

  const pick = (a: SuggestedAction) => (onAction ? onAction(a) : a === 'book_expert' ? setBooking(true) : undefined);
  const actions = (state?.suggestedActions ?? []).filter((a) => onAction || a === 'book_expert');
  const out = !!state && state.quota.remaining <= 0;

  return (
    <section className="qm-advisor" aria-labelledby={`${id}-h`} data-testid="advisor-panel">
      <H id={`${id}-h`} className="qm-advisor__title">{t('audit-remediation.advisor.title')}</H>
      <p className="qm-advisor__intro">{findingTitle ? t('audit-remediation.advisor.introFor', { title: findingTitle }) : t('audit-remediation.advisor.intro')}</p>
      {loadError ? <ErrorState message={t(loadError)} /> : null}
      {!state && !loadError ? <Loading label={t('audit-remediation.advisor.loading')} /> : null}
      {state ? (
        <>
          {state.messages.length ? (
            <ol className="qm-advisor__log" aria-live="polite" aria-label={t('audit-remediation.advisor.logLabel')}>
              {state.messages.map((m) => <MessageItem key={m.id} m={m} locale={locale} t={t} />)}
            </ol>
          ) : <p className="qm-advisor__empty" aria-live="polite">{t('audit-remediation.advisor.empty')}</p>}

          {actions.length ? (
            <div className="qm-advisor__actions" role="group" aria-label={t('audit-remediation.advisor.nextLabel')}>
              {actions.map((a) => <Button key={a} variant="secondary" onClick={() => pick(a)} data-action={a}>{t(`audit-remediation.advisor.action.${a}`)}</Button>)}
            </div>
          ) : null}

          {canAsk ? (
            <form className="qm-advisor__form" onSubmit={submit} noValidate>
              <label className="qm-field__label" htmlFor={`${id}-q`}>{t('audit-remediation.advisor.form.label')}</label>
              <textarea id={`${id}-q`} name="question" className="qm-input qm-advisor__input" rows={3} maxLength={QUESTION_MAX} value={question}
                onChange={(e) => setQuestion(e.target.value)} disabled={pending || out}
                aria-describedby={`${id}-hint${formError ? ` ${id}-err` : ''}`} aria-invalid={formError ? true : undefined} />
              <p className="qm-field__hint" id={`${id}-hint`}>
                {t('audit-remediation.advisor.quota', { remaining: state.quota.remaining, limit: state.quota.questionsLimit })}
              </p>
              {formError ? <p className="qm-field__error" id={`${id}-err`} role="alert">{t(formError)}</p> : null}
              <div className="qm-advisor__submit">
                <Button type="submit" variant="primary" disabled={pending || out}>{pending ? t('audit-remediation.advisor.form.sending') : t('audit-remediation.advisor.form.submit')}</Button>
              </div>
              <p className="qm-advisor__disclaimer">{t('audit-remediation.advisor.disclaimer')}</p>
            </form>
          ) : <p className="qm-advisor__readonly">{t('audit-remediation.advisor.readOnly')}</p>}
        </>
      ) : null}
      {booking ? <BookExpertDialog jobId={jobId} findingId={findingId} defaultTopic={findingTitle ?? ''} onClose={() => setBooking(false)} /> : null}
    </section>
  );
}

function MessageItem({ m, locale, t }: { m: AdvisorMessage; locale: 'en' | 'ar'; t: (k: string, p?: Record<string, string | number>) => string }) {
  const refused = m.flags.includes('refused');
  return (
    <li className="qm-advisor__msg" data-role={m.role}>
      <p className="qm-advisor__who">
        <span>{t(`audit-remediation.advisor.role.${m.role}`)}</span>
        <time dateTime={m.createdAt}>{formatWhen(m.createdAt, locale)}</time>
      </p>
      <p className="qm-advisor__body" dir="auto">{refused ? t('audit-remediation.advisor.note.refused') : m.body}</p>
      {m.role === 'advisor' && m.citations.length ? (
        <ul className="qm-advisor__cites" aria-label={t('audit-remediation.advisor.citations')}>
          {m.citations.map((c) => <li key={c}><Badge tone="neutral"><span dir="ltr">{c}</span></Badge></li>)}
        </ul>
      ) : null}
      {messageNotes(m).map((k) => <p key={k} className="qm-advisor__note">{t(k)}</p>)}
    </li>
  );
}
